import React from 'react';
import { Page, Text, View, StyleSheet, Svg, Path, Circle } from '@react-pdf/renderer';
import { PdfPoweredByAica } from './PoweredByAicaSvg';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, severity, space, typeScale, coverDesign, coverFonts } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';
import { splitOrgName } from '../tokens/coverHelpers';
import { computeBodyWidth, hasLayoutOverrides, PdfBodyWidthProvider, resolveLayout, type PdfLayoutOverrides } from '../tokens/pdfLayout';

export const PdfPage: React.FC<{
    children?: React.ReactNode;
    orgName?: string;
    sectionLabel?: string;
    showHeader?: boolean;
    layout?: PdfLayoutOverrides;
}> = ({ children, orgName, sectionLabel, showHeader = true, layout }) => {
    // Body-width context for full-bleed children (charts). computeBodyWidth
    // returns the historical 520pt when no overrides are set, so the no-
    // override fast-path stays byte-identical at runtime.
    const bodyWidth = computeBodyWidth(layout);
    // Fast path: no overrides → use the static StyleSheet objects untouched
    // so behaviour is byte-identical to the previous implementation. The
    // override branch resolves an effective layout and inlines page paddings
    // + header/footer absolute insets so all three stay aligned when the
    // caller picks A3 or custom paddings.
    if (!hasLayoutOverrides(layout)) {
        return (
            <Page size="A4" style={pdf.page} wrap>
                {showHeader && (
                    <>
                        <View fixed style={pdf.runningHeader}>
                            <Text style={pdf.runningHeaderHalf}>{orgName ?? ''}</Text>
                            <Text style={[pdf.runningHeaderHalf, { textAlign: 'right' }]}>{sectionLabel ?? 'AICA Credit Report'}</Text>
                        </View>
                        <View fixed style={pdf.runningHeaderRule} />
                    </>
                )}
                <PdfBodyWidthProvider bodyWidth={bodyWidth}>{children}</PdfBodyWidthProvider>
                <View fixed style={pdf.runningFooterRule} />
                <View fixed style={pdf.runningFooter}>
                    <Text>AICA Report — Confidential</Text>
                    <Text
                        render={({ pageNumber, totalPages }) =>
                            `${String(pageNumber).padStart(2, '0')} / ${String(totalPages).padStart(2, '0')}`
                        }
                    />
                </View>
            </Page>
        );
    }
    const L = resolveLayout(layout);
    const pageStyle = {
        paddingTop: L.paddingTop,
        paddingBottom: L.paddingBottom,
        paddingLeft: L.paddingLeft,
        paddingRight: L.paddingRight,
    };
    const headerInset = { left: L.paddingLeft, right: L.paddingRight, top: L.headerOffset };
    const headerRule = { left: L.paddingLeft, right: L.paddingRight, top: L.headerOffset + 14 };
    const footerInset = { left: L.paddingLeft, right: L.paddingRight, bottom: L.footerOffset };
    const footerRule = { left: L.paddingLeft, right: L.paddingRight, bottom: L.footerOffset + 18 };
    return (
        <Page size={L.paperSize} style={[pdf.page, pageStyle]} wrap>
            {showHeader && (
                <>
                    <View fixed style={[pdf.runningHeader, headerInset]}>
                        <Text style={pdf.runningHeaderHalf}>{orgName ?? ''}</Text>
                        <Text style={[pdf.runningHeaderHalf, { textAlign: 'right' }]}>{sectionLabel ?? 'AICA Credit Report'}</Text>
                    </View>
                    <View fixed style={[pdf.runningHeaderRule, headerRule]} />
                </>
            )}
            <PdfBodyWidthProvider bodyWidth={bodyWidth}>{children}</PdfBodyWidthProvider>
            <View fixed style={[pdf.runningFooterRule, footerRule]} />
            <View fixed style={[pdf.runningFooter, footerInset]}>
                <Text>AICA Report — Confidential</Text>
                <Text
                    render={({ pageNumber, totalPages }) =>
                        `${String(pageNumber).padStart(2, '0')} / ${String(totalPages).padStart(2, '0')}`
                    }
                />
            </View>
        </Page>
    );
};

export const PdfEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <View style={pdf.eyebrow} wrap={false}>
        <Text style={pdf.eyebrowText}>{children}</Text>
    </View>
);

export const PdfSection: React.FC<{
    kicker?: string;
    title: string;
    subtitle?: string;
    index?: string;
    children: React.ReactNode;
    breakBefore?: boolean;
    minPresenceAhead?: number;
}> = ({ kicker, title, subtitle, index, children, breakBefore, minPresenceAhead }) => {
    const titleFont = fontFamily.display;
    // Default lookahead: keep the section header glued to at least 120pt of body.
    // Without this, the eyebrow + title can land at the bottom of a page and the
    // body starts on the next page, OR the body bleeds into the running footer.
    const presence = minPresenceAhead ?? 120;
    return (
        <View style={pdf.section} break={breakBefore} minPresenceAhead={presence}>
            <View style={pdf.sectionHeader} wrap={false}>
                {kicker ? (
                    <PdfEyebrow>
                        {index ? `${index} · ` : ''}
                        {kicker}
                    </PdfEyebrow>
                ) : null}
                <Text style={[pdf.sectionTitle, { fontFamily: titleFont }]}>{title}</Text>
                {subtitle ? <Text style={pdf.sectionSubtitle}>{subtitle}</Text> : null}
            </View>
            {children}
        </View>
    );
};

export const PdfParagraph: React.FC<{ children: React.ReactNode; style?: object }> = ({ children, style }) => (
    <Text style={[pdf.paragraph, style as any]}>{children}</Text>
);

export const PdfBulletList: React.FC<{ items: string[] }> = ({ items }) => (
    <View>
        {items.map((item, i) => (
            <View key={i} style={pdf.bullet} wrap={false}>
                <Text style={pdf.bulletDot}>—</Text>
                <Text style={pdf.bulletText}>{item}</Text>
            </View>
        ))}
    </View>
);

export const PdfKvList: React.FC<{ items: Array<{ label: string; value?: string | null }> }> = ({ items }) => (
    <View>
        {items
            .filter((x) => x.value != null && x.value !== '')
            .map((x, i) => (
                <View key={i} style={pdf.kvRow} wrap={false}>
                    <Text style={pdf.kvLabel}>{x.label}</Text>
                    <Text style={pdf.kvValue}>{x.value}</Text>
                </View>
            ))}
    </View>
);

export const PdfKpiCard: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
    <View style={pdf.kpiCard} wrap={false}>
        <Text style={pdf.kpiLabel}>{label}</Text>
        <Text style={pdf.kpiValue}>{value}</Text>
        {sub ? <Text style={pdf.kpiSub}>{sub}</Text> : null}
    </View>
);

export const PdfKpiGrid: React.FC<{ items: Array<{ label: string; value: string; sub?: string }> }> = ({ items }) => (
    <View style={pdf.kpiGrid}>
        {items.map((x, i) => (
            <PdfKpiCard key={i} {...x} />
        ))}
    </View>
);

export const PdfChip: React.FC<{ tone: Severity | 'info' | 'brand' | 'muted' | 'accent'; children: React.ReactNode }> = ({
    tone,
    children,
}) => {
    let bg: string = colors.neutralSoft;
    let fg: string = colors.ink40;
    if (tone === 'info') {
        bg = colors.infoSoft;
        fg = colors.info;
    } else if (tone === 'brand' || tone === 'accent') {
        bg = colors.accentSoft;
        fg = colors.accentDeep;
    } else if (tone === 'muted') {
        bg = colors.neutralSoft;
        fg = colors.ink40;
    } else {
        bg = severity[tone].bg;
        fg = severity[tone].fg;
    }
    return <Text style={[pdf.chip, { backgroundColor: bg, color: fg }]}>{children}</Text>;
};

type TableColumn = {
    key: string;
    header: string;
    width?: number | string;
    align?: 'left' | 'right' | 'center';
};

export const PdfDataTable: React.FC<{
    columns: TableColumn[];
    rows: Array<Record<string, React.ReactNode>>;
    zebra?: boolean;
    alignHeaderRight?: string[];
}> = ({ columns, rows, zebra = true }) => {
    const colStyle = (c: TableColumn) => {
        const base: any = { paddingHorizontal: 3 };
        if (typeof c.width === 'number') base.width = c.width;
        else if (typeof c.width === 'string') base.width = c.width;
        else base.flex = 1;
        base.textAlign = c.align ?? 'left';
        if (c.align === 'right') {
            base.fontFamily = fontFamily.mono;
        }
        return base;
    };
    // Per PM feedback on the page-break glitch: previously the table was
    // chunked into 18-row groups and each chunk wrapped in a `wrap={false}`
    // View. With wrapped multi-line cells, a chunk could exceed the page
    // height, and react-pdf's "force this onto next page" handling produced
    // a partial fragment at the top of the previous page (the glitch).
    //
    // New approach: render rows directly, no chunk wrapper. Each row keeps
    // `wrap={false}` so an individual row never splits mid-cell. The table
    // then breaks naturally between rows when a page fills. Header is marked
    // `fixed` so it repeats at the top of each page the table spans.
    return (
        <View style={pdf.table}>
            <View style={pdf.tableHeadRow} fixed>
                {columns.map((c) => (
                    <Text key={c.key} style={[pdf.tableHeadCell, colStyle(c)]}>
                        {c.header}
                    </Text>
                ))}
            </View>
            {rows.map((row, i) => (
                <View key={i} wrap={false} style={[pdf.tableRow, zebra && i % 2 === 1 ? pdf.tableRowAlt : undefined] as any}>
                    {columns.map((c) => (
                        <Text key={c.key} style={[pdf.tableCell, colStyle(c)]} wrap>
                            {row[c.key] as any}
                        </Text>
                    ))}
                </View>
            ))}
        </View>
    );
};

export const PdfDivider: React.FC = () => <View style={pdf.divider} />;

const monthStatusBg = StyleSheet.create({
    on_time: { backgroundColor: colors.pos },
    delayed: { backgroundColor: colors.warn },
    default: { backgroundColor: colors.neg },
    missing: { backgroundColor: colors.neutralSoft },
});

export const PdfMonthGrid: React.FC<{
    title: string;
    primary?: string;
    secondary?: string;
    cells: Array<{ label: string; short: string; yearShort: string; status: 'on_time' | 'delayed' | 'default' | 'missing' }>;
}> = ({ title, primary, secondary, cells }) => (
    <View style={pdf.monthGridWrap} wrap={false}>
        {/* skill rule 9 corollary: align siblings via center+lineHeight:1, not baseline */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={[pdf.h4, { lineHeight: 1 }]}>{title}</Text>
            {primary ? (
                <Text style={{ fontSize: typeScale.caption, color: colors.ink40, lineHeight: 1 }}>
                    <Text style={{ color: colors.ink00, fontFamily: fontFamily.mono, fontWeight: 500 }}>{primary}</Text>
                    {secondary ? ` ${secondary}` : ''}
                </Text>
            ) : null}
        </View>
        <View style={pdf.monthGridRow}>
            {cells.map((c, i) => {
                const bg: any = monthStatusBg[c.status];
                const color = c.status === 'missing' ? colors.ink40 : colors.paper;
                return (
                    <View key={i} style={[pdf.monthCell, bg]} wrap={false}>
                        <Text style={{ fontSize: typeScale.micro, color, fontFamily: fontFamily.body, fontWeight: 600 }}>{c.short}</Text>
                        <Text style={{ fontSize: typeScale.micro - 1, color, opacity: 0.85, fontFamily: fontFamily.mono }}>
                            '{c.yearShort}
                        </Text>
                    </View>
                );
            })}
        </View>
        {/* Legend dots removed — the canonical legend now lives in PdfStatusBar
            below this grid (tinted-pill version per Figma 32:100094). Rendering
            both led to a duplicated row that read as visual noise. */}
    </View>
);

export const PdfSparkBars: React.FC<{ values: number[]; height?: number }> = ({ values, height = 32 }) => {
    const safe = values.map((v) => (Number.isFinite(v) ? v : 0));
    const max = Math.max(1, ...safe);
    return (
        <View style={[pdf.sparkbar, { height }]} wrap={false}>
            {safe.map((v, i) => (
                <View key={i} style={[pdf.sparkbarCol, { height: Math.max(2, (v / max) * height) }]} />
            ))}
        </View>
    );
};

type StatusSegment = { label: string; count: number; color: string };
// Tinted-pill legend for compliance status. Matches Figma 32:100094 — each
// segment is a small tinted square with the status glyph inside, followed by
// an uppercase label. The percentage bar above is unchanged.
const STATUS_LEGEND_TONE: Record<string, { bg: string; fg: string; glyph: string }> = {
    'On time': { bg: '#D1FAE5', fg: '#059669', glyph: '✓' },
    Delayed: { bg: '#FEF3C7', fg: '#D97706', glyph: '!' },
    Default: { bg: '#FEE2E2', fg: '#DC2626', glyph: '✕' },
    Missing: { bg: '#F3F4F6', fg: '#9CA3AF', glyph: '—' },
};

export const PdfStatusBar: React.FC<{ segments: StatusSegment[] }> = ({ segments }) => {
    const total = segments.reduce((s, seg) => s + seg.count, 0);
    if (total <= 0) return null;
    return (
        <View wrap={false}>
            <View style={{ flexDirection: 'row', height: 8, borderRadius: 2, overflow: 'hidden' }}>
                {segments.map((seg, i) =>
                    seg.count > 0 ? (
                        <View
                            key={i}
                            style={{
                                flex: seg.count,
                                backgroundColor: seg.color,
                            }}
                        />
                    ) : null,
                )}
            </View>
            {/* Single horizontal row, never wraps — the four known status buckets fit
                comfortably across the page width. Dropped flexWrap so tight pages
                don't push the trailing pills onto a new line and break the optical
                rhythm. Counts moved off the legend (they're already implicit in the
                percentage bar above) so the row matches Figma 32:100094. */}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 6, alignItems: 'center' }}>
                {segments.map((seg, i) => {
                    if (seg.count <= 0) return null;
                    const tone = STATUS_LEGEND_TONE[seg.label] ?? { bg: seg.color, fg: '#FFFFFF', glyph: '·' };
                    return (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }} wrap={false}>
                            <View
                                style={{
                                    width: 9,
                                    height: 9,
                                    backgroundColor: tone.bg,
                                    borderRadius: 1.5,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 5.5,
                                        color: tone.fg,
                                        fontFamily: fontFamily.sansBold,
                                        lineHeight: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    {tone.glyph}
                                </Text>
                            </View>
                            <Text
                                style={{
                                    fontSize: typeScale.micro,
                                    color: colors.ink40,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                    lineHeight: 1,
                                    fontFamily: fontFamily.sansBold,
                                }}
                            >
                                {seg.label}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

// Sample up to ~`cap` indices across [0..n-1], always including first and last.
// Used for axis label thinning so charts stay readable at 12+ data points.
const sampleAxisIndices = (n: number, cap = 6): number[] => {
    if (n <= 0) return [];
    if (n <= cap) return Array.from({ length: n }, (_, i) => i);
    const out = new Set<number>([0, n - 1]);
    const step = (n - 1) / (cap - 1);
    for (let k = 1; k < cap - 1; k++) out.add(Math.round(k * step));
    return Array.from(out).sort((a, b) => a - b);
};

export const PdfDelayBars: React.FC<{ cells: Array<{ short: string; yearShort: string; delayDays?: number; status: string }> }> = ({
    cells,
}) => {
    const max = Math.max(1, ...cells.map((c) => c.delayDays ?? 0));
    if (max <= 1) return null;
    const yMid = Math.round(max / 2);
    // Show every month label when the series is ≤ 18 (typical compliance window).
    // Beyond that, cap at 12 to avoid overlap on the small chart width.
    const labelIdx = new Set(sampleAxisIndices(cells.length, cells.length <= 18 ? cells.length : 12));

    // SVG viewBox geometry — labels live inside the same SVG (skill rule 11),
    // so x-axis text shares the dot x-coordinate exactly.
    const width = 460;
    const padTop = 9; // room for value labels above the highest dot
    const padBottom = 12; // room for x-axis month labels
    const padLeft = 22;
    const padRight = 6;
    const chartH = 50;
    const height = padTop + chartH + padBottom;
    const chartW = width - padLeft - padRight;
    const xStep = chartW / Math.max(1, cells.length - 1);
    const points = cells.map((c, i) => {
        const v = c.delayDays ?? 0;
        return {
            x: padLeft + i * xStep,
            y: padTop + chartH - (v / max) * chartH,
            v,
            status: c.status,
            short: c.short,
            yearShort: c.yearShort,
        };
    });
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    const dotColor = (status: string, v: number) => (status === 'default' ? colors.neg : v > 0 ? colors.warn : colors.neutralSoft);

    return (
        <View wrap={false} style={{ marginTop: 8 }}>
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                }}
            >
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.ink40,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                        lineHeight: 1,
                    }}
                >
                    Delay days (per month)
                </Text>
                <Text style={{ fontSize: typeScale.micro, color: colors.ink20, fontFamily: fontFamily.mono, lineHeight: 1 }}>
                    Max {max}d
                </Text>
            </View>
            <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                {/* y-axis ticks: max / mid / 0 — gridlines + mono labels in the left gutter */}
                {[
                    { val: max, y: padTop },
                    { val: yMid, y: padTop + chartH / 2 },
                    { val: 0, y: padTop + chartH },
                ].map((t, i) => (
                    <React.Fragment key={`y-${i}`}>
                        <Path
                            d={`M ${padLeft} ${t.y.toFixed(1)} L ${width - padRight} ${t.y.toFixed(1)}`}
                            stroke={colors.hairlineSoft}
                            strokeWidth={0.5}
                        />
                        <Text
                            x={padLeft - 3}
                            y={t.y + 2}
                            style={{ fontSize: 5.5, fontFamily: fontFamily.mono, color: colors.ink40 }}
                            textAnchor="end"
                        >
                            {t.val}
                        </Text>
                    </React.Fragment>
                ))}
                {/* connected line in the dominant warn tone — per-point status is
                    surfaced via the dot color, not the stroke. */}
                <Path d={linePath} stroke={colors.warn} strokeWidth={0.8} fill="none" />
                {points.map((p, i) => (
                    <Circle key={`d-${i}`} cx={p.x} cy={p.y} r={1.6} fill={dotColor(p.status, p.v)} />
                ))}
                {/* value labels above each dot (skip zeros to reduce noise) */}
                {points.map((p, i) =>
                    p.v > 0 ? (
                        <Text
                            key={`v-${i}`}
                            x={p.x}
                            y={p.y - 2.5}
                            style={{ fontSize: 5.5, fontFamily: fontFamily.mono, color: colors.ink40 }}
                            textAnchor="middle"
                        >
                            {p.v}
                        </Text>
                    ) : null,
                )}
                {/* x-axis month labels — same x-coords as the dots (skill rule 11) */}
                {points.map((p, i) =>
                    labelIdx.has(i) ? (
                        <Text
                            key={`x-${i}`}
                            x={p.x}
                            y={padTop + chartH + 8}
                            style={{ fontSize: 5.5, fontFamily: fontFamily.mono, color: colors.ink40 }}
                            textAnchor="middle"
                        >
                            {p.short} '{p.yearShort}
                        </Text>
                    ) : null,
                )}
            </Svg>
        </View>
    );
};

export const PdfCard: React.FC<{
    title: string;
    meta?: string;
    children?: React.ReactNode;
    tone?: 'default' | 'flat' | 'accent' | 'warn';
}> = ({ title, meta, children, tone = 'default' }) => {
    const toneStyle = tone === 'flat' ? pdf.cardFlat : tone === 'accent' ? pdf.cardAccent : tone === 'warn' ? pdf.cardWarn : undefined;
    return (
        <View style={[pdf.card, toneStyle] as any} wrap={false}>
            <Text style={pdf.cardTitle}>{title}</Text>
            {meta ? <Text style={pdf.cardMeta}>{meta}</Text> : null}
            <View>{typeof children === 'string' ? <Text style={pdf.cardBody}>{children}</Text> : children}</View>
        </View>
    );
};

export const PdfCardGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => <View style={pdf.cardGrid}>{children}</View>;

// Properties on `coverDesign` whose values are NOT raw sizes — line-height
// is a unit-less multiplier, *TrackingEm values are em-relative, and the
// pageWidth/Height fields come from the layout resolver (NOT from this
// design token) so we don't want to double-scale them either.
const COVER_NO_SCALE = new Set<string>([
    'titleLineHeight',
    'suffixLineHeight',
    'titleTrackingEm',
    'suffixTrackingEm',
    'aicaWordmarkTrackingEm',
    'metaLabelTrackingEm',
    'bottomLabelTrackingEm',
    'bottomDateTrackingEm',
    'pageWidth',
    'pageHeight',
]);

const scaleCoverDesign = (c: Record<string, unknown>, scale: number): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(c)) {
        out[k] = typeof v === 'number' && !COVER_NO_SCALE.has(k) ? v * scale : v;
    }
    return out;
};

export const PdfCover: React.FC<{
    orgName: string;
    subtitle?: string;
    meta: Array<{ label: string; value: string }>;
    generatedOn: string;
    pnoLogoUrl?: string;
    layout?: PdfLayoutOverrides;
}> = ({ orgName, meta, generatedOn, pnoLogoUrl, layout }) => {
    const { main, suffix } = splitOrgName(orgName);
    const f = coverFonts.pdf;
    // Resolve the effective paper size so A3 / LETTER etc. apply to the cover
    // too. Width/height come from the layout resolver (A4 by default); the
    // inner View height matches so `justifyContent: 'space-between'` still
    // pins the bottom group to the actual page bottom on larger papers.
    const L = resolveLayout(layout);
    const usingOverridePaper = layout?.paperSize != null;
    // Scale all cover typography and spacing proportionally to the paper
    // width (A4=595 → 1.0, A3=842 → ≈1.414). Line-height multipliers and
    // em-relative tracking values are already proportional so they stay
    // unscaled. This keeps the cover visually identical on A3 — just larger
    // — instead of leaving A4-sized text floating in the middle of a bigger
    // page. Default A4 path returns `coverDesign` byte-identical.
    const A4_REF_WIDTH = 595.28;
    const scale = L.pageWidth / A4_REF_WIDTH;
    const c = scale === 1 ? coverDesign : (scaleCoverDesign(coverDesign, scale) as typeof coverDesign);
    const innerHeight = usingOverridePaper ? L.pageHeight : c.pageHeight;

    // Layout strategy (matches what the screen preview does):
    // Wrap the entire cover in a single inner View with an EXPLICIT height equal
    // to the page height. With a known main-axis size, react-pdf's flex layout
    // honours `justifyContent: 'space-between'` reliably — top group stays at the
    // top, bottom group is pinned to the bottom.
    //
    // Why not put padding/flex on <Page> directly? react-pdf's <Page> doesn't
    // propagate its page height to its children's flex container in a way that
    // makes `flexGrow: 1`, `marginTop: 'auto'`, or `position: 'absolute'` behave
    // consistently. An inner View with `height: pageHeight` removes that
    // ambiguity once and for all.
    return (
        <Page size={L.paperSize} wrap={false} style={{ backgroundColor: colors.cover, fontFamily: f.sans }}>
            <View
                style={{
                    height: innerHeight,
                    paddingTop: c.paddingTop,
                    paddingBottom: c.paddingBottom,
                    paddingLeft: c.paddingX,
                    paddingRight: c.paddingX,
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    color: colors.coverInk,
                }}
            >
                {/* TOP GROUP — wordmark, title, rule, metadata. Sits at page top. */}
                <View>
                    {/* Powered-by wordmark. Inlined from src/assets/poweredByAica.svg
                        via the PdfPoweredByAica primitive (Svg + Path) so it
                        bundles into the PDF with no network fetch — avoiding the
                        prior cross-origin <Image> failure that poisoned yoga
                        state and crashed the next module (skill rule 25). */}
                    <PdfPoweredByAica width={c.poweredByLogoWidth} height={c.poweredByLogoHeight} fill={colors.coverInk} />

                    {/* Title */}
                    <View style={{ marginTop: c.topToTitle }}>
                        <Text
                            style={{
                                fontSize: c.titleSize,
                                lineHeight: c.titleLineHeight,
                                letterSpacing: c.titleTrackingEm * c.titleSize,
                                color: colors.coverInk,
                                fontFamily: f.display,
                                fontWeight: 400,
                            }}
                        >
                            {main}
                        </Text>
                        {suffix ? (
                            <Text
                                style={{
                                    fontSize: c.suffixSize,
                                    lineHeight: c.suffixLineHeight,
                                    letterSpacing: c.suffixTrackingEm * c.suffixSize,
                                    color: colors.coverInkSoft,
                                    fontFamily: f.display,
                                    fontWeight: 400,
                                    marginTop: c.suffixMarginTop,
                                }}
                            >
                                {suffix}
                            </Text>
                        ) : null}
                    </View>

                    {/* Teal hairline */}
                    <View
                        style={{
                            width: c.ruleWidth,
                            height: c.ruleHeight,
                            backgroundColor: colors.coverRule,
                            marginTop: c.ruleMarginTop,
                            marginBottom: c.ruleMarginBottom,
                        }}
                    />

                    {/* Metadata rows */}
                    <View>
                        {meta.map((m, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', minHeight: c.metaRowHeight }} wrap={false}>
                                <Text
                                    style={{
                                        width: c.metaLabelWidth,
                                        fontSize: c.metaLabelSize,
                                        fontFamily: f.mono,
                                        fontWeight: 400,
                                        color: colors.coverInkMuted,
                                        letterSpacing: c.metaLabelTrackingEm * c.metaLabelSize,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {m.label}
                                </Text>
                                <Text
                                    style={{
                                        fontSize: c.metaValueSize,
                                        fontFamily: f.mono,
                                        fontWeight: 400,
                                        color: colors.coverInkSoft,
                                        marginLeft: c.metaLabelValueGap,
                                    }}
                                >
                                    {m.value}
                                </Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* BOTTOM GROUP — "Company Due Diligence Report" + generated date.
                    Pinned to the bottom by the parent's `justifyContent: 'space-between'`. */}
                <View wrap={false}>
                    <Text
                        style={{
                            fontSize: c.bottomLabelSize,
                            fontFamily: f.sans,
                            fontWeight: 500,
                            color: colors.coverInk,
                            letterSpacing: c.bottomLabelTrackingEm * c.bottomLabelSize,
                            textTransform: 'uppercase',
                        }}
                    >
                        Company Due Diligence Report
                    </Text>
                    <Text
                        style={{
                            marginTop: c.bottomGap,
                            fontSize: c.bottomDateSize,
                            fontFamily: f.mono,
                            fontWeight: 400,
                            color: colors.coverInkSoft,
                            letterSpacing: c.bottomDateTrackingEm * c.bottomDateSize,
                        }}
                    >
                        Generated · {generatedOn}
                    </Text>
                </View>
            </View>
        </Page>
    );
};
